import { useMemo, useState } from "react";
import { Alert, Form, Spinner } from "react-bootstrap";
import { FaCalendarCheck, FaCheckCircle, FaClock, FaMoneyBillWave, FaTimesCircle } from "react-icons/fa";
import { GiReceiveMoney, GiTakeMyMoney } from "react-icons/gi";
import { useDashboardStats, useBookingTrends, useDriverBookingPerformance, useVehicleBookingCounts, useVehicleCategoryDistribution, useTodayRevenueExpense } from "../../../../api/fleet-management/dashboard/dashboard.hooks";
import BookingTrendsChart from "./_components/BookingTrendsChart";
import DriverBookingPerformanceChart from "./_components/DriverBookingPerformanceChart";
import TodayRevenueExpenseChart from "./_components/TodayRevenueExpenseChart";
import VehicleBookingCountsChart from "./_components/VehicleBookingCountsChart";
import VehicleCategoryDistributionChart from "./_components/VehicleCategoryDistributionChart";
import VehicleAvailabilityCalendar from "./_components/VehicleAvailabilityCalendar";
import StatsRow from "./_components/StatsRow";

const formatDateInput = (date) => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getCurrentMonthRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    startDate: formatDateInput(start),
    endDate: formatDateInput(end),
  };
};

const Dashboard = () => {
  const monthRange = useMemo(() => getCurrentMonthRange(), []);
  const [startDate, setStartDate] = useState(monthRange.startDate);
  const [endDate, setEndDate] = useState(monthRange.endDate);

  const dateParams = useMemo(
    () => ({ startDate, endDate }),
    [startDate, endDate]
  );

  const { data: stats, isLoading, isError, error } = useDashboardStats(dateParams);
  const { data: bookingTrends } = useBookingTrends(dateParams);
  const { data: driverBookingPerformance } = useDriverBookingPerformance(dateParams);
  const { data: vehicleBookingCounts } = useVehicleBookingCounts(dateParams);
  const { data: vehicleCategoryDistribution } = useVehicleCategoryDistribution(dateParams);
  const { data: todayRevenueExpense } = useTodayRevenueExpense(dateParams);

  const bookingStats = [
    {
      key: "confirmedBookings",
      label: "Confirmed Bookings",
      value: stats?.confirmedBookings,
      tone: "info",
      icon: FaCalendarCheck,
    },
    {
      key: "completedBookings",
      label: "Completed Bookings",
      value: stats?.completedBookings,
      tone: "success",
      icon: FaCheckCircle,
    },
    {
      key: "cancelledBookings",
      label: "Cancelled Bookings",
      value: stats?.cancelledBookings,
      tone: "danger",
      icon: FaTimesCircle,
    },
    {
      key: "pendingBookings",
      label: "Pending Bookings",
      value: stats?.pendingBookings,
      tone: "warning",
      icon: FaClock,
    },
  ];

  const moneyStats = [
    {
      key: "totalBookings",
      label: "Total Bookings",
      value: stats?.totalBookings,
      tone: "primary",
      icon: FaCalendarCheck,
    },
    {
      key: "totalEarning",
      label: "Total Earning",
      value: stats?.totalEarning,
      tone: "success",
      icon: FaMoneyBillWave,
    },
    {
      key: "refundedAmount",
      label: "Refunded Amount",
      value: stats?.refundedAmount,
      tone: "danger",
      icon: GiReceiveMoney,
    },
    {
      key: "totalExpenses",
      label: "Total Expenses",
      value: stats?.totalExpenses,
      tone: "warning",
      icon: GiTakeMyMoney,
    },
  ];

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-end align-items-end gap-3 flex-wrap mb-4">
        <div>
          <Form.Label className="mb-1">Start Date</Form.Label>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(event) => setStartDate(event.target.value)}
            style={{ width: 160 }}
          />
        </div>
        <div>
          <Form.Label className="mb-1">End Date</Form.Label>
          <Form.Control
            type="date"
            value={endDate}
            min={startDate}
            onChange={(event) => setEndDate(event.target.value)}
            style={{ width: 160 }}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" />
        </div>
      ) : isError ? (
        <Alert variant="danger" className="mb-0">
          Failed to load dashboard stats.{" "}
          {error?.response?.data?.message || "Please try again."}
        </Alert>
      ) : (
        <>
          <StatsRow title="Booking Overview" stats={bookingStats} />
          <StatsRow
            title="Money & Expense Overview"
            stats={moneyStats}
            isCurrency
          />
        </>
      )}

      <VehicleAvailabilityCalendar startDate={startDate} endDate={endDate} />
      <BookingTrendsChart data={bookingTrends} />
      <TodayRevenueExpenseChart data={todayRevenueExpense} />
      <DriverBookingPerformanceChart data={driverBookingPerformance} />
      <VehicleBookingCountsChart data={vehicleBookingCounts} />
      <VehicleCategoryDistributionChart data={vehicleCategoryDistribution} />
    </div>
  );
};

export default Dashboard;
